import { brand } from '@metorial/brand';
import { Paths } from '@metorial/frontend-config';
import { Menu, theme } from '@metorial/ui';
import { RiArrowDownSLine, RiArrowLeftSLine } from '@remixicon/react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import type { ShellInstance, ShellProject, ShellScope } from './scope';

// Top row of the settings area (cloud-spec §7):
//   ‹ Back to BlackOS · [project ▾] · [environment ▾]          [Search settings…]
// Switching the project or environment keeps you on the same settings page
// when it belongs to a project/environment, and opens the project's profile
// otherwise.

let Row = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

let BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  height: 34px;
  padding: 0 10px 0 4px;
  border-radius: 9px;
  color: ${theme.colors.gray700};
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  text-decoration: none;

  svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
  }

  &:hover,
  &:focus-visible {
    background: ${theme.colors.gray400};
    color: ${theme.colors.foreground};
  }
`;

let Divider = styled.span`
  width: 1px;
  height: 18px;
  margin: 0 4px;
  background: ${theme.colors.gray500};
  flex-shrink: 0;
`;

let SelectButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 8px 0 10px;
  border: 1px solid ${theme.colors.gray400};
  border-radius: 9px;
  background: ${theme.colors.background};
  color: ${theme.colors.gray900};
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;

  small {
    font-size: 12px;
    font-weight: 500;
    color: ${theme.colors.gray600};
  }

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    color: ${theme.colors.gray650};
  }

  &:hover,
  &:focus-visible,
  &[data-state='open'] {
    background: ${theme.colors.gray200};
    border-color: ${theme.colors.gray500};
  }
`;

let SETTINGS_SCOPED = /^\/o\/([^/]+)\/project\/([^/]+)(?:\/instance\/([^/]+))?(\/.*)?$/;

let environmentName = (instance: ShellInstance) =>
  instance.type == 'production' ? 'Production' : instance.name;

let pickInstance = (scope: ShellScope, project: ShellProject) => {
  let instances = (scope.boot.data?.instances ?? []).filter(i => i.project.id == project.id);
  return instances.find(i => i.type == 'production') ?? instances[0] ?? null;
};

export let SettingsTopBar = ({ scope }: { scope: ShellScope }) => {
  let { pathname } = useLocation();
  let navigate = useNavigate();
  let { organization, project, instance } = scope;

  let projects = (scope.boot.data?.projects ?? []).filter(
    p => p.organizationId == organization?.id
  );
  let instances = (scope.boot.data?.instances ?? []).filter(i => i.project.id == project?.id);

  let goTo = (nextProject: ShellProject, nextInstance: ShellInstance | null) => {
    let match = pathname.match(SETTINGS_SCOPED);

    if (match && organization) {
      let [, orgSlug, , instanceSlug, rest = ''] = match;
      let base = `/o/${orgSlug}/project/${nextProject.slug}`;
      if (instanceSlug && nextInstance) base += `/instance/${nextInstance.slug}`;
      return navigate(base + rest);
    }

    navigate(Paths.organization.instance(organization, nextProject, nextInstance));
  };

  let backTo = instance
    ? Paths.instance.home(organization, project, instance)
    : '/';

  return (
    <Row>
      <BackLink to={backTo}>
        <RiArrowLeftSLine />
        Back to {brand.name}
      </BackLink>

      {project && (
        <>
          <Divider aria-hidden />

          <Menu
            label="Select project"
            items={projects.map(p => ({
              id: p.id,
              label: p.id == project.id ? `${p.name} (current)` : p.name
            }))}
            onItemClick={id => {
              let next = projects.find(p => p.id == id);
              if (!next || next.id == project.id) return;
              goTo(next, pickInstance(scope, next));
            }}
          >
            <SelectButton type="button" aria-label="Select project" title="Project">
              {project.name}
              <RiArrowDownSLine />
            </SelectButton>
          </Menu>
        </>
      )}

      {project && instance && (
        <Menu
          label="Select environment"
          items={instances.map(i => ({
            id: i.id,
            label: i.id == instance.id ? `${environmentName(i)} (current)` : environmentName(i)
          }))}
          onItemClick={id => {
            let next = instances.find(i => i.id == id);
            if (!next || next.id == instance.id) return;
            goTo(project, next);
          }}
        >
          <SelectButton type="button" aria-label="Select environment" title="Environment">
            {environmentName(instance)}
            <RiArrowDownSLine />
          </SelectButton>
        </Menu>
      )}
    </Row>
  );
};
